"use client"

import { usePage } from "@inertiajs/react"
import { useQueryClient } from "@tanstack/react-query"
import { Bell } from "lucide-react"
import { useEffect } from "react"
import { toast } from "sonner"

import {
  type NotificationItem,
  notificationQueryKeys,
  notificationsApi,
} from "@/lib/api/notifications"
import { getCableConsumer } from "@/lib/cable"
import type { SharedData } from "@/types"

interface NotificationsChangedMessage {
  type?: string
}

export default function LiveNotificationSubscriber() {
  const page = usePage<SharedData>()
  const queryClient = useQueryClient()
  const isSignedIn = !!page.props.auth?.user

  useEffect(() => {
    if (!isSignedIn) {
      return
    }

    const consumer = getCableConsumer()
    if (!consumer) {
      return
    }

    const subscription = consumer.subscriptions.create(
      { channel: "NotificationsChannel" },
      {
        async received(message: NotificationsChangedMessage) {
          if (message.type !== "notifications.changed") {
            return
          }

          const latest = await notificationsApi.latest()
          if (shouldToast(latest.notification)) {
            showNotificationToast(latest.notification)
          }

          void queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.summary,
          })
          void queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.list,
          })
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [isSignedIn, queryClient])

  return null
}

function shouldToast(notification: NotificationItem | null) {
  return notification?.kind === "new_feed_post"
}

function showNotificationToast(notification: NotificationItem | null) {
  toast.custom(() => (
    <div className="border-border bg-card text-card-foreground w-[min(22rem,calc(100vw-2rem))] border p-4 shadow-[0_16px_44px_rgba(45,32,24,0.16)]">
      <div className="flex items-start gap-3">
        <span className="bg-accent text-accent-foreground mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center">
          <Bell className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-garamond text-foreground text-[17px] font-semibold leading-tight">
            {notification?.title ?? "New notification"}
          </p>
          <p className="text-muted-foreground mt-1 line-clamp-2 text-sm leading-relaxed">
            {notification?.body ?? "Your Book Cove notifications have been updated."}
          </p>
        </div>
      </div>
    </div>
  ))
}
